import { useState } from "react"
import { toast } from "sonner"
import { fetchWithAuth } from "@/lib/api"
import { buildOpenWorkspaceUri, launchCursorUri } from "@/lib/launch-cursor-uri"

export interface ProjectNotLinkedInfo {
  projectName: string | null
  basecampProjectId: string | null
}

export interface UseOpenInCursorOptions {
  todoId?: string | null
  todoTitle?: string | null
  projectName?: string | null
  projectSlug?: string | null
  /** Basecamp bucket/project id - lets the API resolve a project even when projectName doesn't match the DB (e.g. folder browsing). */
  basecampProjectId?: string | null
  documentId?: string | null
  /**
   * Called instead of throwing a generic error when the API reports `project_not_linked`.
   * Callers should open `WorkspaceLinkDialog` with this info, then call `handleOpenInCursor()`
   * again once the user has linked the project.
   */
  onProjectNotLinked?: (info: ProjectNotLinkedInfo) => void
}

export interface OpenInCursorHandlerOptions {
  /** When false, generate + copy the workspace URI without launching Cursor (default true). */
  launch?: boolean
  /**
   * Per-call overrides for callers that don't have a single stable target (e.g. the document
   * viewer's More menu, where "Open in Cursor" applies to whichever tab is active). Falls back
   * to the values bound at `useOpenInCursor(...)` call time when omitted.
   */
  todoId?: string | null
  todoTitle?: string | null
  projectName?: string | null
  projectSlug?: string | null
  basecampProjectId?: string | null
  documentId?: string | null
  onProjectNotLinked?: (info: ProjectNotLinkedInfo) => void
}

export interface UseOpenInCursorResult {
  isGenerating: boolean
  /** cursor:// URI (client delivery mode) or `cursor -n "..."` command (path delivery mode) from the last successful run. */
  workspaceUri: string | null
  handleOpenInCursor: (options?: OpenInCursorHandlerOptions) => Promise<void>
}

/**
 * Imperative Open-in-Cursor (no React state). Used by context menus and by `useOpenInCursor`.
 * Returns the workspace URI / command string on success, or null on failure / cancel.
 */
export async function openInCursorWorkspace(
  options: OpenInCursorHandlerOptions,
): Promise<string | null> {
  const launch = options.launch !== false
  const todoId = options.todoId
  const todoTitle = options.todoTitle
  const projectName = options.projectName
  const projectSlug = options.projectSlug
  const basecampProjectId = options.basecampProjectId
  const documentId = options.documentId
  const onProjectNotLinked = options.onProjectNotLinked

  if (!todoId) {
    toast.error("Cannot open workspace", {
      description: "Todo ID is required to generate workspace",
    })
    return null
  }

  try {
    const response = await fetchWithAuth(`/api/workspace/generate`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        todoId,
        todoTitle: todoTitle || undefined,
        projectName: projectName || undefined,
        projectSlug: projectSlug || undefined,
        basecampProjectId: basecampProjectId || undefined,
        documentId: documentId || undefined,
      }),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: "Unknown error" }))
      throw new Error(errorData.error || errorData.message || `HTTP ${response.status}`)
    }

    const data = await response.json()

    if (!data.success && data.reason === "project_not_linked") {
      const info: ProjectNotLinkedInfo = {
        projectName: projectName || data.projectName || null,
        basecampProjectId: basecampProjectId || data.basecampProjectId || null,
      }
      if (onProjectNotLinked) {
        onProjectNotLinked(info)
      } else {
        toast.error("Project isn't linked to a git repo yet", {
          description: "Link this project to a local git repo, then try again.",
          duration: 8000,
        })
      }
      return null
    }

    if (!data.success) {
      throw new Error(data.error || data.message || "Failed to generate workspace")
    }

    let resultUri: string | null = null

    if (data.deliveryMode === "client") {
      const uriParams = new URLSearchParams()
      uriParams.set("todoId", String(todoId))
      if (todoTitle) uriParams.set("todoTitle", todoTitle)
      if (projectName) uriParams.set("projectName", projectName)
      if (projectSlug) uriParams.set("projectSlug", projectSlug)
      if (basecampProjectId) uriParams.set("basecampProjectId", String(basecampProjectId))
      if (documentId) uriParams.set("documentId", documentId)
      if (data.folderPath) uriParams.set("folderPath", data.folderPath)

      const uri = buildOpenWorkspaceUri(uriParams)
      resultUri = uri
      await navigator.clipboard.writeText(uri).catch(() => {})

      if (launch) {
        launchCursorUri(uri)
        toast.success("Opening workspace in Cursor", {
          description:
            "Link copied. If Cursor did not open, run CMD+Shift+P: Maater: Open Workspace from Clipboard Link.",
          duration: 10000,
        })
      } else {
        toast.success("Workspace link copied", {
          description: uri,
          duration: 8000,
        })
      }
    } else {
      const command = `cursor -n "${data.workspacePath}"`
      resultUri = data.workspaceUri ?? command
      await navigator.clipboard.writeText(command).catch(() => {})
      toast.success("Command copied - new Cursor window", {
        description: "Paste in Terminal to open in a new Cursor window.",
        duration: 8000,
      })
    }

    if (data.gitPathMissing) {
      const bcId = basecampProjectId || data.basecampProjectId || null
      toast.warning("Opened without a linked git repository", {
        description: bcId
          ? "This workspace has task context only (no code). Configure the project's git repository to include the codebase."
          : "This workspace has task context only (no code). Configure the repo path in project settings to include the codebase.",
        duration: 10000,
        action: bcId
          ? {
              label: "Configure git repository",
              onClick: () => {
                window.dispatchEvent(
                  new CustomEvent("basecamp-project-configure-git", { detail: { projectId: bcId } }),
                )
              },
            }
          : undefined,
      })
    }

    return resultUri
  } catch (error) {
    console.error("[OpenInCursor] Error generating workspace:", error)
    toast.error("Failed to generate workspace", {
      description: error instanceof Error ? error.message : "Unknown error",
      duration: 8000,
    })
    return null
  }
}

/**
 * Generates a FlowNotes Cursor workspace for a todo folder and opens/copies it.
 * Shared between `OpenInCursorButton`, `CursorWorkspaceMenu`, and any inline / menu-item
 * callers that need the same generate-workspace + cursor:// launch flow without duplicating
 * the fetch logic across the folder toolbar, document viewer, and Basecamp todo header.
 */
export function useOpenInCursor({
  todoId: boundTodoId,
  todoTitle: boundTodoTitle,
  projectName: boundProjectName,
  projectSlug: boundProjectSlug,
  basecampProjectId: boundBasecampProjectId,
  documentId: boundDocumentId,
  onProjectNotLinked,
}: UseOpenInCursorOptions): UseOpenInCursorResult {
  const [isGenerating, setIsGenerating] = useState(false)
  const [workspaceUri, setWorkspaceUri] = useState<string | null>(null)

  const handleOpenInCursor = async (options?: OpenInCursorHandlerOptions) => {
    setIsGenerating(true)
    setWorkspaceUri(null)
    try {
      const uri = await openInCursorWorkspace({
        launch: options?.launch,
        todoId: options?.todoId ?? boundTodoId,
        todoTitle: options?.todoTitle ?? boundTodoTitle,
        projectName: options?.projectName ?? boundProjectName,
        projectSlug: options?.projectSlug ?? boundProjectSlug,
        basecampProjectId: options?.basecampProjectId ?? boundBasecampProjectId,
        documentId: options?.documentId ?? boundDocumentId,
        onProjectNotLinked: options?.onProjectNotLinked ?? onProjectNotLinked,
      })
      if (uri) setWorkspaceUri(uri)
    } finally {
      setIsGenerating(false)
    }
  }

  return { isGenerating, workspaceUri, handleOpenInCursor }
}
